import i18nFactory from '@yandex-int/i18n';

import {
    CanvasApiValidationErrors,
    VideoAdditionApiResponse,
    VideoAdditionPresetApiResponse
} from '../common/requestsTypings';
import { CreativeType, SharedData } from '../common/typings';
import {
    CommonOptionGroupName,
    CommonOptionName,
    CreativeOptionData,
    CreativeOptionGroupApiValue,
    CreativeOptionsConfig,
    CreativeOptionsData,
    CreativeOptionsErrors,
    CreativeOptionsGroupValue,
    CreativeOptionsValue,
    CreativeOptionType,
    VideoOptionGroupName
} from '../CreativeOptions/typings';

// tslint:disable-next-line no-external-i18n-import
import * as keyset from './AddCreativePage.i18n';
import { getAddCreativeFileEditorId } from './helpers';

const i18n = i18nFactory(keyset);

export function mapVideoCreativeOptionsValueFromApi(
    options: CreativeOptionGroupApiValue[],
    config: CreativeOptionsConfig
): CreativeOptionsValue {
    return options.map(
        (group: CreativeOptionGroupApiValue): CreativeOptionsGroupValue => {
            switch (group.type) {
                case CommonOptionGroupName.image:
                case CommonOptionGroupName.logo:
                    throw 'Unsupported parameter type!';

                default:
                    break;
            }

            const groupOptions = { ...group.options };
            const groupOptionsConfig = config[group.type].options;

            // При получении данных креатива если какое-то значение не задано и его можно редактировать, подставляем defaultValue
            groupOptionsConfig.forEach((optionConfig: CreativeOptionData) => {
                if (!groupOptions[optionConfig.name] && optionConfig.editable && optionConfig.defaultValue) {
                    groupOptions[optionConfig.name] = optionConfig.defaultValue;
                }
            });

            return {
                type: group.type,
                options: groupOptions,
                visible: group.available,
                // Для видеокреативов параметр предупреждение скрываем
                externalHidden: group.type === VideoOptionGroupName.disclaimer
            };
        }
    );
}

export function mapVideoCreativeOptionsValueToApi(
    options: CreativeOptionsValue,
    isCreateRequest?: boolean
): CreativeOptionGroupApiValue[] {
    return options.map((group: CreativeOptionsGroupValue) => {
        switch (group.type) {
            case CommonOptionGroupName.image:
            case CommonOptionGroupName.logo:
                throw 'Unsupported parameter type!';

            default:
                break;
        }

        const groupOptions = { ...group.options };

        // При отправке запроса на бек надо вычистить значения, которые подставлялись при получении данных превью
        if (group.optionsFromPreviewData) {
            group.optionsFromPreviewData.forEach((name: string) => {
                delete groupOptions[name];
            });
        }

        if (!isCreateRequest) {
            // При получении видео превью для нового креатива подставляем в значения текстовых полей значение placeholder
            Object.keys(groupOptions).forEach((optionName: string) => {
                if (groupOptions[optionName] !== '') {
                    return;
                }

                if (optionName === CreativeOptionType.Text && groupOptions.placeholder) {
                    groupOptions[optionName] = groupOptions.placeholder;
                    delete groupOptions.placeholder;
                }
            });
        }

        // при этом нужно удалять все placeholder при отправке запроса на бек
        Object.keys(groupOptions).forEach(() => {
            if (groupOptions.placeholder) {
                delete groupOptions.placeholder;
            }
        });

        return {
            type: group.type,
            options: groupOptions,
            available: Boolean(group.visible)
        };
    });
}

export function mapValidationErrorToOptionsError(
    errors: CanvasApiValidationErrors,
    values: CreativeOptionsValue,
    isVideo?: boolean
): CreativeOptionsErrors {
    return Object.keys(errors).reduce((acc: CreativeOptionsErrors, errorPath: string) => {
        // Вытаскиваем из строки вида
        // elements[0]options.video_id или items[0].data.elements[1].options.content
        // id-шник группы параметров и имя параметра
        const match = errorPath.match(/(?:items\[(\d+)\])?(?:\.data)?(?:\.?elements\[(\d+)\])?(?:\.?(\w+))*/i);

        if (!match) {
            return acc;
        }

        if (!match[0]) {
            return acc;
        }

        let optionName = match[3];

        // в коммон креативах по пути items[0].data.options.backgroundColor лежат ошибки для common опций
        if (!isVideo && match[2] === undefined) {
            acc[CommonOptionGroupName.common] = {
                ...acc[CommonOptionGroupName.common],
                [optionName]: errors[errorPath].map((text: string) => ({
                    text
                }))
            };

            return acc;
        }

        let groupIndex = Number(match[2]);

        // в коммон креативах мы вторым параметром вставляем common опции
        if (!isVideo && values.find(value => value.type === CommonOptionGroupName.common) && groupIndex >= 1) {
            groupIndex++;
        }

        const group = values[groupIndex];

        if (!isVideo && (group.type === CommonOptionGroupName.image || group.type === CommonOptionGroupName.logo)) {
            optionName = CommonOptionName.fileId;
        }

        acc[group.type] = {
            ...acc[group.type],
            [optionName]: errors[errorPath].map((text: string) => ({
                text
            }))
        };

        return acc;
    }, {});
}

function getNameForVideoCreative(sharedData: SharedData): string {
    if (
        sharedData.cpc ||
        sharedData.cpm ||
        sharedData.mobile_content_video ||
        sharedData.cpm_indoor ||
        sharedData.cpm_outdoor
    ) {
        return i18n('video-ad-name');
    } else if (sharedData.cpm_audio) {
        return i18n('video-cpm-audio-name');
    } else {
        return i18n('video-addition-name');
    }
}

export function mapVideoCreativeOptionsConfigFromApi(
    config: CreativeOptionsConfig,
    creativeType: CreativeType,
    id: string
): CreativeOptionsConfig {
    Object.keys(config).forEach((groupName: string) => {
        const groupConfig = config[groupName];

        groupConfig.options.forEach((option: CreativeOptionData) => {
            if (option.type === CreativeOptionType.File) {
                option.editorId = getAddCreativeFileEditorId(creativeType, id, groupName, option.name);
            }
        });
    });

    return config;
}

export function mapVideoCreativeOptionsFromApi(
    response: VideoAdditionApiResponse | VideoAdditionPresetApiResponse,
    isNewFromPreset: boolean,
    sharedData: SharedData,
    requestId: string
): CreativeOptionsData {
    if (!sharedData.creativeType) {
        throw new Error('creativeType is undefined');
    }

    return {
        name: isNewFromPreset ? getNameForVideoCreative(sharedData) : response.name,
        optionsConfig: mapVideoCreativeOptionsConfigFromApi(response.config, sharedData.creativeType, requestId),
        optionsValue: mapVideoCreativeOptionsValueFromApi(response.data.elements, response.config)
    };
}

export function mapOverlayCreativeOptionsFromApi(
    response: VideoAdditionApiResponse | VideoAdditionPresetApiResponse,
    isNewFromPreset: boolean,
    sharedData: SharedData,
    requestId: string
): CreativeOptionsData {
    if (!sharedData.creativeType) {
        throw new Error('creativeType is undefined');
    }

    return {
        name: isNewFromPreset ? i18n('overlay-name') : response.name,
        optionsConfig: mapVideoCreativeOptionsConfigFromApi(response.config, sharedData.creativeType, requestId),
        optionsValue: mapVideoCreativeOptionsValueFromApi(response.data.elements, response.config)
    };
}
